import axios from 'axios';
import JwtManager from '../utils/jwtManager';
import eHttpResponse from '../assets/enums/eHttpResponse';
import eNotificationType from '../assets/enums/eNotificationType';
import { toast } from 'react-toastify';
import { showLoader, hideLoader } from '../utils/LoaderContext'; // Ensure the correct path
import { config } from '../../core/config';
import { isPublicPath, ROUTES } from '../../app/routes';
import { installPlatformApiAdapter } from './platformApiAdapter';

let isHandlingUnauthorized = false;

const isBackgroundRequest = (url?: string) => {
  if (!url) return false;

  return (
    url.includes('/api/print/job/payload') ||
    url.includes('/api/print/done') ||
    url.includes('/api/print/fail')
  );
};

const handleResponseMessage = (
  message: string,
  notificationType: eNotificationType
) => {
  switch (notificationType) {
    case eNotificationType.Success:
      toast.info(message, {
        autoClose: 1000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
        progress: 0,
      });
      break;
    case eNotificationType.Error:
      toast.error(message, {
        autoClose: 1000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
        progress: 0,
      });
      break;
    default:
      throw new Error('handleResponseMessage: Notification Type not handled');
  }
};

const axiosInstance = axios.create({
  /*
   * The Platform API v1 base (`/api/v1/platform` by default, VITE_API_BASE_URL otherwise).
   *
   * The legacy constants in `ApiConstants` were written against the old monolith's root
   * (`/api/user/list`, `/api/utility/roles`, ...). Those Platform-owned entries are
   * retargeted to PlatformAPI's v1 surface, and this baseURL makes the retargeted values
   * relative to it, so a constant is just `organization/memberships` rather than a full
   * path.
   */
  baseURL: config.apiBaseUrl,
});

/**
 * Whether this request raised the global loader, recorded on the request itself.
 *
 * The response side must lower the loader exactly when the request side raised it. Deciding
 * both from `skipLoader` is not safe, because the flag can change between the two: the adapter
 * used to be installed first, so its request mapping ran AFTER the loader interceptor (see the
 * install note at the bottom) and set `skipLoader` on the known-missing routes once the loader
 * was already raised. The response then skipped lowering it, the pending-request counter never
 * returned to zero, and on `/` the full-screen loader covered the landing forever.
 */
const LOADER_SHOWN = '__loaderShown';

const markLoaderShown = (request: object) => {
  (request as Record<string, unknown>)[LOADER_SHOWN] = true;
};

const lowerLoaderFor = (request: unknown) => {
  if (request && (request as Record<string, unknown>)[LOADER_SHOWN]) {
    (request as Record<string, unknown>)[LOADER_SHOWN] = false;
    hideLoader();
  }
};

/** The readable message of a failed response, including PlatformAPI's problem details. */
const errorMessageOf = (data: any): string | undefined =>
  data?.message || data?.Message || data?.error || data?.detail || data?.title;

const handleUnauthorized = () => {
  if (isHandlingUnauthorized) return;
  isHandlingUnauthorized = true;

  JwtManager.clearToken();
  window.sessionStorage.removeItem('companyTimeZone');

  /*
   * The Platform owns the session: clearing it is enough for `RequireAuth` to hand the
   * operator back to /login. Dispatching the legacy redux invalidations is unnecessary
   * here (the copied slices are rebuilt from the Platform session on the next mount) and
   * would need the legacy store to exist, which it may not while a page is unmounting.
   *
   * A PUBLIC page must not be redirected, and that guard is load-bearing now that `/` renders
   * without a session. The landing is the same component signed in and signed out, and its
   * feed/notification reads answer 401 to a visitor — without this check the first of those
   * would throw the visitor at the sign-in screen, so `/` could never actually be public.
   */
  if (isPublicPath(window.location.pathname)) return;

  if (!window.location.pathname.startsWith(ROUTES.login)) {
    window.location.assign(ROUTES.login);
  }

  setTimeout(() => {
    isHandlingUnauthorized = false;
  }, 1500);
};

axiosInstance.interceptors.request.use(
  (request) => {
    if (isBackgroundRequest(request.url)) {
      (request as any).skipLoader = true;
    }

    if (!(request as any).skipLoader) {
      showLoader();
      markLoaderShown(request);
    }
    const jwt = JwtManager.accessToken;
    if (jwt) {
      request.headers['Authorization'] = `Bearer ${jwt}`;
    }
    return request;
  },
  (error) => {
    lowerLoaderFor(error?.config);
    if (!(error?.config as any)?.skipErrorToast) {
      handleResponseMessage('Request error. Please try again.', eNotificationType.Error);
    }
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    lowerLoaderFor(response.config);
    return response;
  },
  (error) => {
    const skipErrorToast = !!(error?.config as any)?.skipErrorToast;
    lowerLoaderFor(error?.config);

    if (error.response) {
      if (error.response.status === eHttpResponse.Unauthorized) {
        if (JwtManager.accessToken) {
          handleUnauthorized();
        }
      } else if (!skipErrorToast && errorMessageOf(error.response.data)) {
        handleResponseMessage(errorMessageOf(error.response.data) as string, eNotificationType.Error);
      } else if (!skipErrorToast && error.response.status === eHttpResponse.NotFound) {
        handleResponseMessage('Requested resource not found', eNotificationType.Error);
      } else if (!skipErrorToast) {
        handleResponseMessage('An error occurred. Please try again.', eNotificationType.Error);
      }
    } else if (!skipErrorToast) {
      handleResponseMessage('Network error. Please check your connection.', eNotificationType.Error);
    }
    return Promise.reject(error);
  }
);

/*
 * Installed AFTER the auth/loader interceptors above, and the position matters.
 *
 * axios runs REQUEST interceptors in reverse registration order (last registered runs first;
 * `transitional.legacyInterceptorReqResOrdering` defaults to true) and RESPONSE interceptors in
 * registration order. Installing the adapter last therefore means its request mapping runs
 * first, so the `skipLoader` / `skipErrorToast` flags it sets on the known-missing routes are
 * already there when the loader interceptor looks. On the response side the loader/toast
 * interceptor runs first; it only reads those flags and the raw error body, never the mapped
 * shape, and the ported screens still receive the adapter's mapped response.
 *
 * The loader bookkeeping above does not depend on this order, so a future axios that flips it
 * would at worst flash the spinner on a known-missing route, never leave it stuck.
 */
installPlatformApiAdapter(axiosInstance);

export default axiosInstance;
